import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api/boilerMatrixApi';

const MATRIX_METHODS = ['RT', 'UT', 'PT', 'MT', 'VT', 'MLO', 'VISUAL', 'OTHER'];

const emptyForm = () => ({
  groupName: '',
  subgroup: '',
  item: '',
  methods: [],
  inspectionDescription: '',
  frequency: '',
});

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Something went wrong.';

const definitionFromLine = (line) => ({
  groupName: line.groupName || '',
  subgroup: line.subgroup || '',
  item: line.item || '',
  methods: line.methods || [],
  inspectionDescription: line.inspectionDescription || '',
  frequency: line.frequency || '',
});

export default function BoilerMatrixManager({ boiler, boilers, onClose, onChanged }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [orderDirty, setOrderDirty] = useState(false);
  const [sourceBoilerId, setSourceBoilerId] = useState('');
  const [sourceLines, setSourceLines] = useState([]);
  const [sourceGroup, setSourceGroup] = useState('ALL');
  const [selectedSourceIds, setSelectedSourceIds] = useState([]);
  const [sourceLoading, setSourceLoading] = useState(false);

  const loadManagement = useCallback(async () => {
    const data = await api.getMatrixManagement(boiler.id);
    setLines(Array.isArray(data) ? data : []);
    setOrderDirty(false);
  }, [boiler.id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.getMatrixManagement(boiler.id)
      .then((data) => {
        if (!cancelled) setLines(Array.isArray(data) ? data : []);
      })
      .catch((requestError) => {
        if (!cancelled) setError(getErrorMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [boiler.id]);

  useEffect(() => {
    if (!sourceBoilerId) {
      setSourceLines([]);
      setSelectedSourceIds([]);
      setSourceGroup('ALL');
      return undefined;
    }
    let cancelled = false;
    setSourceLoading(true);
    setError('');
    api.getMatrix(sourceBoilerId)
      .then((data) => {
        if (!cancelled) {
          setSourceLines(Array.isArray(data) ? data : []);
          setSelectedSourceIds([]);
          setSourceGroup('ALL');
        }
      })
      .catch((requestError) => {
        if (!cancelled) setError(getErrorMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) setSourceLoading(false);
      });
    return () => { cancelled = true; };
  }, [sourceBoilerId]);

  const activeLines = useMemo(() => lines.filter((line) => line.active), [lines]);
  const archivedLines = useMemo(() => lines.filter((line) => !line.active), [lines]);
  const visibleActiveLines = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return activeLines;
    return activeLines.filter((line) => [
      line.groupName,
      line.subgroup,
      line.item,
      line.inspectionDescription,
      line.frequency,
    ].filter(Boolean).join(' ').toLowerCase().includes(query));
  }, [activeLines, search]);
  const sourceGroups = useMemo(
    () => Array.from(new Set(sourceLines.map((line) => line.groupName))).filter(Boolean),
    [sourceLines]
  );
  const visibleSourceLines = useMemo(
    () => sourceLines.filter((line) => sourceGroup === 'ALL' || line.groupName === sourceGroup),
    [sourceLines, sourceGroup]
  );
  const otherBoilers = boilers.filter((candidate) => candidate.id !== boiler.id);
  const existingGroups = Array.from(new Set(activeLines.map((line) => line.groupName))).filter(Boolean);

  const runMutation = async (operation, successAction) => {
    try {
      setSaving(true);
      setError('');
      await operation();
      await loadManagement();
      await onChanged();
      if (successAction) successAction();
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  const beginAdd = () => {
    setEditingLine(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const beginEdit = (line) => {
    setEditingLine(line);
    setForm(definitionFromLine(line));
    setShowForm(true);
  };

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const toggleMethod = (method) => {
    setForm((current) => ({
      ...current,
      methods: current.methods.includes(method)
        ? current.methods.filter((value) => value !== method)
        : [...current.methods, method],
    }));
  };

  const saveDefinition = async (event) => {
    event.preventDefault();
    const payload = {
      ...form,
      groupName: form.groupName.trim(),
      subgroup: form.subgroup.trim(),
      item: form.item.trim(),
      inspectionDescription: form.inspectionDescription.trim(),
      frequency: form.frequency.trim(),
    };
    if (!payload.groupName || !payload.item) {
      setError('Inspection area and matrix line are required.');
      return;
    }
    await runMutation(
      () => editingLine
        ? api.updateMatrixLineDefinition(editingLine.id, payload)
        : api.createMatrixLine(boiler.id, payload),
      () => {
        setShowForm(false);
        setEditingLine(null);
        setForm(emptyForm());
      }
    );
  };

  const moveLine = (lineId, offset) => {
    const currentIndex = activeLines.findIndex((line) => line.id === lineId);
    const nextIndex = currentIndex + offset;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= activeLines.length) return;
    const nextActive = [...activeLines];
    [nextActive[currentIndex], nextActive[nextIndex]] = [nextActive[nextIndex], nextActive[currentIndex]];
    setLines([...nextActive, ...archivedLines]);
    setOrderDirty(true);
  };

  const saveOrder = () => runMutation(
    () => api.reorderMatrixLines(boiler.id, activeLines.map((line) => line.id))
  );

  const archiveLine = (line) => {
    const evidenceCount = line.evidence?.length || 0;
    const confirmed = window.confirm(
      `Archive “${line.item}”? It will leave the live matrix, but ${evidenceCount || 'any'} linked evidence record${evidenceCount === 1 ? '' : 's'} and audit history will be retained.`
    );
    if (confirmed) runMutation(() => api.archiveMatrixLine(line.id));
  };

  const restoreLine = (line) => runMutation(() => api.restoreMatrixLine(line.id));

  const toggleSourceLine = (lineId) => setSelectedSourceIds((current) =>
    current.includes(lineId) ? current.filter((id) => id !== lineId) : [...current, lineId]
  );

  const selectVisibleSourceLines = () => {
    const visibleIds = visibleSourceLines.map((line) => line.id);
    const everySelected = visibleIds.length > 0 && visibleIds.every((id) => selectedSourceIds.includes(id));
    setSelectedSourceIds((current) => everySelected
      ? current.filter((id) => !visibleIds.includes(id))
      : Array.from(new Set([...current, ...visibleIds]))
    );
  };

  const copySelected = () => {
    if (!sourceBoilerId || selectedSourceIds.length === 0) {
      setError('Select a source boiler and at least one matrix line to copy.');
      return;
    }
    runMutation(
      () => api.copyMatrixLines(boiler.id, Number(sourceBoilerId), selectedSourceIds),
      () => setSelectedSourceIds([])
    );
  };

  return (
    <div className="boiler-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="boiler-modal boiler-modal-wide matrix-manager-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Manage ${boiler.name} matrix`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="boiler-modal-header matrix-manager-header">
          <div>
            <span className="boiler-modal-eyebrow">ADMIN MATRIX SETUP</span>
            <h2>Manage inspection matrix</h2>
            <p>{boiler.name} has its own saved copy of the current ODIN template.</p>
          </div>
          <button type="button" className="boiler-icon-button" onClick={onClose} aria-label="Close">×</button>
        </div>

        {error && <div className="boiler-error matrix-manager-error">{error}</div>}
        {loading ? <div className="boiler-loading">Loading matrix setup…</div> : (
          <div className="matrix-manager-layout">
            <section className="matrix-manager-panel matrix-manager-lines">
              <div className="matrix-manager-panel-heading">
                <div>
                  <h3>Active matrix lines</h3>
                  <p>{activeLines.length} lines shown in the inspection matrix.</p>
                </div>
                <div className="matrix-manager-heading-actions">
                  <button type="button" className="boiler-button secondary" disabled={!orderDirty || saving} onClick={saveOrder}>Save order</button>
                  <button type="button" className="boiler-button" disabled={saving} onClick={beginAdd}>+ Add line</button>
                </div>
              </div>
              <input
                className="matrix-manager-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search active lines…"
              />
              <div className="matrix-manager-line-list">
                {visibleActiveLines.map((line) => {
                  const absoluteIndex = activeLines.findIndex((candidate) => candidate.id === line.id);
                  return (
                    <article className="matrix-manager-line" key={line.id}>
                      <div className="matrix-manager-line-order">
                        <button type="button" disabled={saving || absoluteIndex === 0} onClick={() => moveLine(line.id, -1)} aria-label={`Move ${line.item} up`}>↑</button>
                        <span>{absoluteIndex + 1}</span>
                        <button type="button" disabled={saving || absoluteIndex === activeLines.length - 1} onClick={() => moveLine(line.id, 1)} aria-label={`Move ${line.item} down`}>↓</button>
                      </div>
                      <div className="matrix-manager-line-copy">
                        <span>{line.groupName}{line.subgroup ? ` · ${line.subgroup}` : ''}</span>
                        <strong>{line.item}</strong>
                        <small>{line.methods?.join(', ') || 'No methods'}{line.customLine ? ' · Custom line' : ' · Template line'}</small>
                      </div>
                      <div className="matrix-manager-line-actions">
                        <button type="button" disabled={saving} onClick={() => beginEdit(line)}>Edit</button>
                        <button type="button" className="danger" disabled={saving} onClick={() => archiveLine(line)}>Archive</button>
                      </div>
                    </article>
                  );
                })}
                {visibleActiveLines.length === 0 && <p className="boiler-help">No active lines match this search.</p>}
              </div>
            </section>

            <aside className="matrix-manager-side">
              {showForm && (
                <form className="matrix-manager-panel matrix-manager-form" onSubmit={saveDefinition}>
                  <div className="matrix-manager-panel-heading">
                    <div>
                      <h3>{editingLine ? 'Edit matrix line' : 'Add matrix line'}</h3>
                      <p>{editingLine ? 'Update this boiler’s saved copy.' : 'Create a custom line for this boiler.'}</p>
                    </div>
                    <button type="button" className="matrix-manager-text-button" onClick={() => setShowForm(false)}>Cancel</button>
                  </div>
                  <label>Inspection area<input list="matrix-manager-groups" value={form.groupName} onChange={(event) => updateForm('groupName', event.target.value)} required /></label>
                  <datalist id="matrix-manager-groups">{existingGroups.map((name) => <option value={name} key={name} />)}</datalist>
                  <label>Subgroup<input value={form.subgroup} onChange={(event) => updateForm('subgroup', event.target.value)} /></label>
                  <label>Matrix line<textarea rows="2" value={form.item} onChange={(event) => updateForm('item', event.target.value)} required /></label>
                  <fieldset>
                    <legend>Inspection methods</legend>
                    <div className="matrix-manager-methods">
                      {MATRIX_METHODS.map((method) => <label key={method}><input type="checkbox" checked={form.methods.includes(method)} onChange={() => toggleMethod(method)} />{method}</label>)}
                    </div>
                  </fieldset>
                  <label>Inspection description<textarea rows="3" value={form.inspectionDescription} onChange={(event) => updateForm('inspectionDescription', event.target.value)} /></label>
                  <label>Frequency<input value={form.frequency} onChange={(event) => updateForm('frequency', event.target.value)} /></label>
                  <button type="submit" className="boiler-button" disabled={saving}>{saving ? 'Saving…' : editingLine ? 'Save changes' : 'Add matrix line'}</button>
                </form>
              )}

              <section className="matrix-manager-panel matrix-manager-copy-panel">
                <div className="matrix-manager-panel-heading">
                  <div><h3>Copy from another boiler</h3><p>Copies definitions only; report evidence stays with its original boiler.</p></div>
                </div>
                {otherBoilers.length === 0 ? <p className="boiler-help">Add another boiler at this location to reuse its lines or inspection sections.</p> : (
                  <>
                    <label>Source boiler<select value={sourceBoilerId} onChange={(event) => setSourceBoilerId(event.target.value)}><option value="">Select boiler</option>{otherBoilers.map((candidate) => <option value={candidate.id} key={candidate.id}>{candidate.name}</option>)}</select></label>
                    {sourceBoilerId && (
                      <>
                        <label>Inspection section<select value={sourceGroup} onChange={(event) => setSourceGroup(event.target.value)}><option value="ALL">All sections</option>{sourceGroups.map((name) => <option value={name} key={name}>{name}</option>)}</select></label>
                        <button type="button" className="matrix-manager-text-button" disabled={sourceLoading || visibleSourceLines.length === 0} onClick={selectVisibleSourceLines}>Select / clear displayed section</button>
                        <div className="matrix-manager-source-list">
                          {sourceLoading ? <p>Loading source matrix…</p> : visibleSourceLines.map((line) => (
                            <label key={line.id}><input type="checkbox" checked={selectedSourceIds.includes(line.id)} onChange={() => toggleSourceLine(line.id)} /><span><strong>{line.item}</strong><small>{line.groupName}{line.subgroup ? ` · ${line.subgroup}` : ''}</small></span></label>
                          ))}
                        </div>
                        <button type="button" className="boiler-button full" disabled={saving || selectedSourceIds.length === 0} onClick={copySelected}>Copy {selectedSourceIds.length || ''} selected line{selectedSourceIds.length === 1 ? '' : 's'}</button>
                      </>
                    )}
                  </>
                )}
              </section>

              <section className="matrix-manager-panel matrix-manager-archive-panel">
                <div className="matrix-manager-panel-heading"><div><h3>Archived lines</h3><p>{archivedLines.length} retained for history and evidence links.</p></div></div>
                <div className="matrix-manager-archive-list">
                  {archivedLines.map((line) => (
                    <div key={line.id}><span><strong>{line.item}</strong><small>{line.groupName}{line.archivedBy ? ` · Archived by ${line.archivedBy}` : ''}</small></span><button type="button" disabled={saving} onClick={() => restoreLine(line)}>Restore</button></div>
                  ))}
                  {archivedLines.length === 0 && <p className="boiler-help">No matrix lines are archived.</p>}
                </div>
              </section>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
